import type { MapDef } from './index.js'

// ─── Lobby seat count ────────────────────────────────────────────────────────
/**
 * Seat-count bounds and default for a game lobby, shared by the client stepper,
 * the create endpoint and the settings endpoint so the three cannot disagree.
 *
 * They used to: the stepper clamped 2–6, `PATCH /settings` validated 2–6, and
 * `POST /games` accepted 1 and defaulted an omitted value to 6. So the default
 * was the ceiling — a size almost no game is actually played at, which the host
 * then had to step down on nearly every create.
 */
export const PLAYER_SEATS_MIN = 2
export const PLAYER_SEATS_MAX = 9

/** Fixed boards retain their own seat count; every other board needs a territory per player. */
export function mapPlayerCapacity(map?: Pick<MapDef, 'territories' | 'fixedSetup'> | null): number {
  if (!map) return PLAYER_SEATS_MAX
  return Math.min(PLAYER_SEATS_MAX, map.fixedSetup?.playerCount ?? map.territories.length)
}
export const PLAYER_SEATS_DEFAULT = 4
/**
 * The table sizes a map allows. A board-dictated opening (MapDef.fixedSetup)
 * allows minPlayers..playerCount — e.g. Royal Rumble 2–9, Hexcore 2–6 — and an
 * exact-seat board (no minPlayers) only its playerCount. Other maps allow
 * PLAYER_SEATS_MIN up to their capacity.
 */
export function mapSeatRange(map?: Pick<MapDef, 'territories' | 'fixedSetup'> | null): { min: number; max: number } {
  const fixed = map?.fixedSetup
  if (fixed) return { min: fixed.minPlayers ?? fixed.playerCount, max: fixed.playerCount }
  return { min: PLAYER_SEATS_MIN, max: mapPlayerCapacity(map) }
}
